import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PlayCircle, TrendingUp, Users, Award } from 'lucide-react';

const Portfolio: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: PlayCircle, value: '500K+', label: 'Total Views' },
    { icon: TrendingUp, value: '15%+', label: 'Avg. Engagement Rate' },
    { icon: Users, value: '50K+', label: 'Community Reach' },
    { icon: Award, value: '20+', label: 'Brand Collaborations' }
  ];

  const collaborations = [
    {
      brand: 'TED',
      type: 'Educational Content',
      description: 'Created presentation breakdown content analyzing TED talks, focusing on communication techniques and delivery methods.',
      image: '/lovable-uploads/9e4789d1-233d-48a3-82c0-70b4fc7c0729.png',
      results: '50K+ views, 18% engagement rate'
    },
    {
      brand: 'Corporate Training Partners',
      type: 'Professional Development',
      description: 'Developed video series on overcoming presentation anxiety and mastering body language for corporate audiences.',
      image: '/lovable-uploads/7bd1b569-be19-4c60-9432-3ee718943918.png',
      results: '30K+ views, 22% engagement rate'
    },
    {
      brand: 'Leadership Academy',
      type: 'Executive Communication',
      description: 'Produced content exploring the neuroscience of storytelling and how leaders can use narrative to inspire teams.',
      image: '/lovable-uploads/4debc272-1e4c-4c48-b265-355b18470137.png',
      results: '40K+ views, 16% engagement rate'
    }
  ];

  const recentContent = [
    {
      title: 'How to use intonation while presenting',
      platform: 'TikTok',
      views: '75K',
      engagement: '18%',
      thumbnail: '/lovable-uploads/9e4789d1-233d-48a3-82c0-70b4fc7c0729.png'
    },
    {
      title: 'How to Prevent Nervous Shaking',
      platform: 'TikTok',
      views: '60K',
      engagement: '22%',
      thumbnail: '/lovable-uploads/7bd1b569-be19-4c60-9432-3ee718943918.png'
    },
    {
      title: 'Neurochemistry of Storytelling',
      platform: 'TikTok',
      views: '85K',
      engagement: '16%',
      thumbnail: '/lovable-uploads/4debc272-1e4c-4c48-b265-355b18470137.png'
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-presintention-accent/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" />
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-presintention-accent/10 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container-width">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}>
            <span className="inline-block px-3 py-1 bg-presintention-accent/10 text-presintention-accent rounded-full text-sm font-medium mb-4">
              UGC Creator Portfolio
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-presintention-dark">
              Alex Fairlie
              <span className="block text-presintention-accent mt-2">Content Creator & Coach</span>
            </h1>
            <p className="text-lg md:text-xl text-presintention-dark/70 max-w-3xl mx-auto">
              Creating authentic, educational content that helps professionals master the art of presentation and communication. Specializing in executive presence, public speaking, and performance coaching.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container-width">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className={`text-center p-6 rounded-lg bg-presintention-light transition-all duration-700 hover-lift ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-presintention-accent" />
                <div className="text-3xl font-bold text-presintention-dark mb-1">{stat.value}</div>
                <div className="text-sm text-presintention-muted">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-presintention-light">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-x-8'}`}>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-presintention-accent/20 to-transparent rounded-2xl transform rotate-3"></div>
                <img 
                  src="/lovable-uploads/8a1d8ca9-a01f-4aae-9ab9-2d6d648a37c1.png"
                  alt="Alex Fairlie"
                  className="relative z-10 w-full rounded-2xl shadow-xl"
                />
              </div>
            </div>

            <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}>
              <span className="inline-block px-3 py-1 bg-presintention-accent/10 text-presintention-accent rounded-full text-sm font-medium mb-4">
                About Me
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-presintention-dark">
                Bridging Performance & Communication
              </h2>
              <div className="space-y-4 text-presintention-dark/70">
                <p>
                  With over 20 years as a professional actor and a decade of experience in sales training and management, I bring a unique perspective to content creation. My background in nonverbal communication and human behavior allows me to break down complex presentation techniques into actionable insights.
                </p>
                <p>
                  I create authentic, research-backed content that demystifies public speaking and helps professionals at all levels communicate with confidence and clarity. My content combines theatrical performance techniques with psychological principles to deliver real results.
                </p>
                <p>
                  Specializing in executive presence, storytelling, body language, and overcoming presentation anxiety, I've helped thousands of professionals transform their communication skills through engaging, bite-sized video content.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Collaborations */}
      <section className="py-20 bg-white">
        <div className="container-width">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-presintention-accent/10 text-presintention-accent rounded-full text-sm font-medium mb-4">
              Recent Work
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-presintention-dark">
              Brand Collaborations
            </h2>
          </div>

          <div className="space-y-8">
            {collaborations.map((collab, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-presintention-light rounded-2xl p-8 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <img 
                    src={collab.image}
                    alt={collab.brand}
                    className="w-full h-64 object-cover rounded-lg shadow-lg"
                  />
                </div>
                <div>
                  <div className="text-sm text-presintention-accent font-medium mb-2">{collab.type}</div>
                  <h3 className="text-2xl font-bold text-presintention-dark mb-3">{collab.brand}</h3>
                  <p className="text-presintention-dark/70 mb-4">{collab.description}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <Award className="w-4 h-4 text-presintention-accent" />
                    <span className="font-medium text-presintention-dark">{collab.results}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Content */}
      <section className="py-20 bg-presintention-light">
        <div className="container-width">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-presintention-accent/10 text-presintention-accent rounded-full text-sm font-medium mb-4">
              Latest Posts
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-presintention-dark">
              Recent Content
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {recentContent.map((content, index) => (
              <div 
                key={index}
                className={`bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-700 hover-lift ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <img 
                  src={content.thumbnail}
                  alt={content.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="text-xs text-presintention-accent font-medium mb-2">{content.platform}</div>
                  <h3 className="text-lg font-bold text-presintention-dark mb-3">{content.title}</h3>
                  <div className="flex items-center justify-between text-sm text-presintention-muted">
                    <span className="flex items-center gap-1">
                      <PlayCircle className="w-4 h-4" />
                      {content.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      {content.engagement} engagement
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container-width">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-presintention-dark mb-6">
              Let's Create Together
            </h2>
            <p className="text-lg text-presintention-dark/70 mb-8">
              Interested in collaborating or working together? I'd love to hear about your project and explore how we can create impactful content that resonates with your audience.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-presintention-accent text-white rounded-md hover:bg-opacity-90 transition-all button-transition hover-lift text-base font-medium"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Portfolio;
